import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { auth } from '../firebase';
import {
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userTier, setUserTier] = useState(null); // 'viewer' | 'user' | 'admin' | null
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_URL = process.env.NODE_ENV === 'production'
    ? ''
    : (process.env.REACT_APP_API_URL || `http://${window.location.hostname}:3151`);

  const getToken = useCallback(async () => {
    if (currentUser) {
      try {
        return await currentUser.getIdToken();
      } catch (err) {
        console.error('Error getting token:', err);
        return null;
      }
    }
    return null;
  }, [currentUser]);

  const verifyWithServer = useCallback(async (user) => {
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${API_URL}/api/auth/verify`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Verification failed');
      }

      return await response.json();
    } catch (err) {
      throw err;
    }
  }, [API_URL]);

  const login = useCallback(async () => {
    setError(null);
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        hd: 'azragames.com' // Restrict to company domain
      });
      const result = await signInWithPopup(auth, provider);
      const verifyResult = await verifyWithServer(result.user);
      setUserTier(verifyResult.user?.tier || null);
      return result.user;
    } catch (err) {
      setError(err.message);
      // Sign out if verification failed
      await signOut(auth);
      throw err;
    }
  }, [verifyWithServer]);

  const logout = useCallback(async () => {
    setError(null);
    try {
      await signOut(auth);
      setCurrentUser(null);
      setUserTier(null);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const verifyResult = await verifyWithServer(user);
          setCurrentUser(user);
          setUserTier(verifyResult.user?.tier || null);
        } catch (err) {
          console.error('Auth state verification failed:', err);
          setCurrentUser(null);
          setUserTier(null);
          setError(err.message);
        }
      } else {
        setCurrentUser(null);
        setUserTier(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, [verifyWithServer]);

  const value = {
    currentUser,
    userTier,
    isUser: userTier === 'user' || userTier === 'admin',
    isAdmin: userTier === 'admin',
    loading,
    error,
    login,
    logout,
    getToken
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
